(function (global) {
  const INSTANCE_KEY = Symbol("customScrollbarInstance");
  const DEFAULT_MIN_THUMB_SIZE = 32;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class CustomScrollbar {
    constructor(container, options = {}) {
      this.container = container;
      this.options = {
        viewport: null,
        minThumbSize: DEFAULT_MIN_THUMB_SIZE,
        ...options,
      };

      this.viewport =
        this.options.viewport ||
        container.querySelector("[data-scroll-viewport]") ||
        container;
      if (!this.viewport) {
        throw new Error(
          "CustomScrollbar requires a scrollable viewport element.",
        );
      }

      this.container.classList.add("custom-scroll");
      this.viewport.classList.add("custom-scroll__viewport");

      this.track = container.querySelector("[data-scroll-track]");
      if (!this.track) {
        this.track = document.createElement("div");
        this.track.className = "custom-scroll__track";
        this.track.setAttribute("aria-hidden", "true");
        container.appendChild(this.track);
      }

      this.thumb = this.track.querySelector("[data-scroll-thumb]");
      if (!this.thumb) {
        this.thumb = document.createElement("div");
        this.thumb.className = "custom-scroll__thumb";
        this.thumb.setAttribute("data-scroll-thumb", "");
        this.track.appendChild(this.thumb);
      } else {
        this.thumb.classList.add("custom-scroll__thumb");
      }

      this.track.setAttribute("data-scroll-track", "");

      this._dragging = false;
      this._pointerId = null;
      this._startPointerY = 0;
      this._startThumbTop = 0;
      this._thumbTop = 0;
      this._thumbHeight = this.options.minThumbSize;
      this._maxThumbTop = 0;
      this._maxScrollTop = 0;

      this.container.setAttribute("data-scroll-ready", "true");

      this.viewport.addEventListener("scroll", this._handleScroll, {
        passive: true,
      });
      this.thumb.addEventListener("pointerdown", this._handleThumbPointerDown);
      this.track.addEventListener("pointerdown", this._handleTrackPointerDown);
      window.addEventListener("resize", this._handleResize, { passive: true });

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.viewport);
        const firstChild = this.viewport.firstElementChild;
        if (firstChild) {
          this.resizeObserver.observe(firstChild);
        }
      }

      this.update();
    }

    update() {
      const { scrollHeight, clientHeight } = this.viewport;
      const trackHeight =
        this.track.clientHeight || this.container.clientHeight;
      const hasScrollableContent = scrollHeight > clientHeight + 1;

      if (!hasScrollableContent) {
        this.container.classList.remove("custom-scroll--active");
        this.container.classList.add("custom-scroll--hidden");
        this.thumb.style.transform = "translateY(0)";
        return;
      }

      this.container.classList.add("custom-scroll--active");
      this.container.classList.remove("custom-scroll--hidden");

      const rawThumbHeight = (clientHeight / scrollHeight) * trackHeight;
      const thumbHeight = clamp(
        rawThumbHeight,
        this.options.minThumbSize,
        trackHeight,
      );

      this._thumbHeight = thumbHeight;
      this._trackHeight = trackHeight;
      this._maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
      this._maxScrollTop = Math.max(scrollHeight - clientHeight, 0);

      this.thumb.style.height = `${thumbHeight}px`;

      this._syncThumbToScroll();
    }

    refresh() {
      this.update();
    }

    destroy() {
      this.viewport.removeEventListener("scroll", this._handleScroll);
      this.thumb.removeEventListener(
        "pointerdown",
        this._handleThumbPointerDown,
      );
      this.track.removeEventListener(
        "pointerdown",
        this._handleTrackPointerDown,
      );
      window.removeEventListener("resize", this._handleResize);
      document.removeEventListener("pointermove", this._handlePointerMove);
      document.removeEventListener("pointerup", this._handlePointerUp);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.container.classList.remove(
        "custom-scroll",
        "custom-scroll--active",
        "custom-scroll--hidden",
      );
      this.viewport.classList.remove("custom-scroll__viewport");
      this.container.removeAttribute("data-scroll-ready");
    }

    _syncThumbToScroll() {
      const { scrollTop } = this.viewport;
      const thumbTop = this._maxScrollTop
        ? (scrollTop / this._maxScrollTop) * this._maxThumbTop
        : 0;
      this._thumbTop = clamp(thumbTop, 0, this._maxThumbTop);
      this.thumb.style.transform = `translateY(${this._thumbTop}px)`;
    }

    _scrollToThumbPosition(thumbTop) {
      const clampedTop = clamp(thumbTop, 0, this._maxThumbTop);
      this._thumbTop = clampedTop;
      const ratio = this._maxThumbTop ? clampedTop / this._maxThumbTop : 0;
      const targetScrollTop = ratio * this._maxScrollTop;
      this.viewport.scrollTop = targetScrollTop;
      this.thumb.style.transform = `translateY(${this._thumbTop}px)`;
    }

    _handleScroll = () => {
      if (!this._dragging) {
        this._syncThumbToScroll();
      }
    };

    _handleResize = () => {
      this.update();
    };

    _handleThumbPointerDown = (event) => {
      event.stopPropagation();
      event.preventDefault();

      this._dragging = true;
      this._pointerId = event.pointerId;
      this._startPointerY = event.clientY;
      this._startThumbTop = this._thumbTop;

      this.thumb.classList.add("is-dragging");
      if (this.thumb.setPointerCapture) {
        this.thumb.setPointerCapture(event.pointerId);
      }

      document.addEventListener("pointermove", this._handlePointerMove);
      document.addEventListener("pointerup", this._handlePointerUp);
    };

    _handleTrackPointerDown = (event) => {
      if (event.target === this.thumb) {
        return;
      }
      event.preventDefault();

      const rect = this.track.getBoundingClientRect();
      const offsetY = event.clientY - rect.top - this._thumbHeight / 2;
      this._scrollToThumbPosition(offsetY);
    };

    _handlePointerMove = (event) => {
      if (!this._dragging || event.pointerId !== this._pointerId) {
        return;
      }
      event.preventDefault();
      const delta = event.clientY - this._startPointerY;
      const nextTop = this._startThumbTop + delta;
      this._scrollToThumbPosition(nextTop);
    };

    _handlePointerUp = (event) => {
      if (event.pointerId !== this._pointerId) return;

      this._dragging = false;
      this._pointerId = null;
      this.thumb.classList.remove("is-dragging");
      if (this.thumb.releasePointerCapture) {
        this.thumb.releasePointerCapture(event.pointerId);
      }

      document.removeEventListener("pointermove", this._handlePointerMove);
      document.removeEventListener("pointerup", this._handlePointerUp);

      this._syncThumbToScroll();
    };
  }

  function enhance(element, options) {
    if (!element) return null;
    if (element[INSTANCE_KEY]) {
      element[INSTANCE_KEY].update();
      return element[INSTANCE_KEY];
    }
    const instance = new CustomScrollbar(element, options);
    element[INSTANCE_KEY] = instance;
    return instance;
  }

  function init(root = document) {
    const elements = root.querySelectorAll("[data-custom-scroll]");
    const instances = [];
    elements.forEach((el) => {
      const viewportSelector = el.getAttribute("data-scroll-target");
      const viewport = viewportSelector
        ? el.querySelector(viewportSelector)
        : el.querySelector("[data-scroll-viewport]") || el;
      instances.push(enhance(el, { viewport }));
    });
    return instances;
  }

  function refresh(el) {
    if (!el) return;
    const instance = el[INSTANCE_KEY];
    if (instance) {
      instance.update();
    }
  }

  function refreshAll(root = document) {
    const elements = root.querySelectorAll("[data-custom-scroll]");
    elements.forEach((el) => refresh(el));
  }

  global.CustomScrollbars = {
    enhance,
    init,
    refresh,
    refreshAll,
  };
})(typeof window !== "undefined" ? window : globalThis);
